import { PropertyTiles } from './../../components/properties/property-tiles';
import { useState, useEffect, useMemo } from "react";
import PropertyAPI from "../../api/property/property-api-factory";
import LoadingAnimation from "../../components/loading/loading";
import { AddNewPropertyModal } from '../../components/properties/add-new-property-modal';
import _ from "lodash";
import { Button } from 'react-bootstrap';
import { useMsal, } from '@azure/msal-react';
import { CompanyAPI } from '../../api/company/company-api-factory';

import { PropertyFunctions } from '../../api/property/property-functions';

import './property.css';
import { arrayAny } from '../../utils/arrayUtils';

export const PropertySearch = (props) => {


    const { instance, inProgress, accounts } = useMsal();
    const [data, setData] = useState([]);
    const [fetchComplete, setFetchComplete] = useState(false);
    const [fetchInProgress, setFetchInProgress] = useState(false);
    const [searchInProgress, setSearchInProgress] = useState(false);
    const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
    const [landlords, setLandlords] = useState([]);

    const [searchTerm, setSearchTerm] = useState("");
    const [currentlySearchingForString, setCurrentlySearchingForString] = useState("");
    const [showFileUpload, setShowFileUpload] = useState(false);

    const api = useMemo(() => new PropertyAPI(instance, inProgress, accounts, false), [instance, inProgress, accounts]);
    const companyApi = useMemo(() => new CompanyAPI(instance, inProgress, accounts, true), [instance, inProgress, accounts]);

    const fetchData = () => {
        if (!fetchInProgress && searchTerm === "") {
            if (!fetchComplete) {
                setFetchInProgress(true);
                setFetchComplete(false);
                api.getTopN(20)
                    .then(data => {
                        setData(data);
                        setFetchComplete(true);
                    }).catch(error => {
                        setData(null);
                        setFetchComplete(true);
                        setFetchInProgress(false);
                        console.log(error)
                    });
            }
        }
    };

    const search = () => {
        if (!searchInProgress && searchTerm && searchTerm.trim().length > 2 && currentlySearchingForString !== searchTerm) {

            setCurrentlySearchingForString(searchTerm);

            const intermediateResult = [];

            for (const property of data) {

                let match = false;

                for (const memberName in property) {
                    let member = property[memberName].toString();

                    if (member.search(new RegExp(searchTerm, "i")) > -1) {
                        match = true;
                        break;
                    }
                }

                if (match) {
                    intermediateResult.push(property);
                    match = false;
                }
            }

            if (arrayAny(intermediateResult)) {
                setData(intermediateResult);
            }

            setSearchInProgress(true);
            setFetchComplete(false);

            api.find(searchTerm)
                .then((data) => {
                    setData(data);
                })
                .catch(error => console.log("An error occurred searching for properties:", error))
                .finally(() => {
                    setSearchInProgress(false);
                    setFetchComplete(true);
                    setCurrentlySearchingForString("");
                });
        }
        else if (!searchInProgress && searchTerm.length === 0 && currentlySearchingForString.length > 0) {
            reset();
        }
    };

    const handleSetSearchTerm = (searchTerm) => {
        setSearchTerm(searchTerm);
        if (searchTerm === "") {
            reset();
        }
    };

    var throttledSetSearchTerm = _.debounce(handleSetSearchTerm, 500, { leading: true, trailing: false, maxWait: 1000 });
    const throttledFetch = _.debounce(fetchData, 200, { leading: true, trailing: false, maxWait: 1000 });

    useEffect(() => {
        throttledFetch();
    });

    useEffect(() => {
        search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    useEffect(() => {
        if (!showAddPropertyModal) return;
        if (landlords && landlords.length > 0) return;

        companyApi.getAllLandlords()
            .then(data => setLandlords(data || []))
            .catch(err => {
                console.log('An error occurred loading landlords:', err);
                setLandlords([]);
            });
    }, [showAddPropertyModal, landlords, companyApi]);


    const handleCancel = () => {
        setShowAddPropertyModal(false);
    };

    const handleSave = (data) => {

        const propertyModel = PropertyFunctions.createPropertyModel(data);

        api.createProperty(propertyModel)
            .then(result => {
                console.log("Save result", result);
                setData(current => {
                    current = [].concat(current, result);
                    return current;
                });
            })
            .catch(error => {
                console.log("An error occured adding new property.", error)
            })
            .finally(() => {
                setShowAddPropertyModal(false);
            });
    };

    const handleShowModal = () => setShowAddPropertyModal(true);

    const reset = () => {
        setSearchTerm("");
        setCurrentlySearchingForString("");
        setSearchInProgress(false);
        setFetchInProgress(false);
        setFetchComplete(false);
        setData(null);
        throttledFetch();
    }

    const isHeaderRow = row => {

        if (row.length === 6) {
            return row[0] === "Name" && row[1] === "Owner" && row[2] === "DoorNumberOrName" && row[3] === "RoadName" && row[4] === "Town" && row[5] === "PostalCode";
        }
        else {
            return false;
        }

    }

    const bulkSave = (file) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            const csvData = e.target.result; // Read the CSV data
            const propertyArray = csvData.split('\r\n').map((line) => line.split(','));

            let count = 0;
            const newProperties = [];

            for (const row of propertyArray) {
                //Skip header and empty rows
                if ((count === 0 || propertyArray[count].length) && isHeaderRow(propertyArray[count])) {
                    count++;
                    continue;
                }
                else {
                    count++;
                    if (row[0].length > 0 && row.length !== 6) {
                        throw new Error("There must be 6 fields on each row.");
                    }
                    else if (row[0].length > 0) {
                        const newProperty = { name: row[0], landlordOrOwner: row[1], doorNumberOrName: row[2], roadName: row[3], town: row[4], postalCode: row[5] };
                        newProperties.push(newProperty);
                    }
                }
            }

            api.bulkCreateProperties(newProperties)
                .then(result => {
                    setData(current => {
                        current = [].concat(current, _.take(result, 10));
                        return _.take(current, 20);
                    });
                })
                .catch(error => console.log("Error saving in bulk", error))
                .finally(() => setShowFileUpload(false));
        };

        reader.readAsText(file); // Read the file as text
    };

    const handleShowFileUpload = () => {
        setShowFileUpload(!showFileUpload);
    };

    return (
        <>
            <AddNewPropertyModal
                show={showAddPropertyModal}
                handleCancel={handleCancel}
                handleSave={handleSave}
                landlords={landlords}
            />
            <div className={'pos-customer'} >
                <div className="pos-content">
                    <Button className="col-md-3 col-lg-2 add-property btn btn-success btn-rounded px-4 rounded-pill ms-20px" variant="primary" onClick={handleShowModal}>
                        <i className="fa fa-plus fa-lg me-2 ms-n2 "></i>Add Single Property
                    </Button>


                    <Button className="col-md-3 col-lg-2 add-property btn btn-success btn-rounded px-4 rounded-pill ms-20px me-10px" variant="primary" onClick={handleShowFileUpload}>
                        <i className={`fa ${!showFileUpload ? "fa-plus" : "fa-x"} fa-lg me-2 ms-n2`}></i>{showFileUpload ? "Cancel" : "Bulk Add Properties"}
                    </Button>
                    {showFileUpload && <input type='file' accept='.csv' onChange={e => bulkSave(e.target.files[0])} />}
                    <div className="search-bar-container">
                        <div className="search-bar-form">
                            <div className="search-bar">
                                <input onChange={e => throttledSetSearchTerm(e.target.value)} value={searchTerm} type="text" className="form-control" placeholder="Search..." />
                                <button onClick={() => { reset(); }} title="Cancel" className="btn btn-search"><i className="fa fa-x"></i></button>
                            </div>
                        </div>
                    </div>

                    {(!fetchComplete) && <LoadingAnimation />}

                    {!searchInProgress && arrayAny(data) &&
                        (<div className="pos-content-container" options={{ suppressScrollX: true }}>
                            <div className="product-row">
                                <PropertyTiles properties={data} />
                            </div>
                        </div>)
                    }

                    {!searchInProgress && !arrayAny(data) && !!fetchComplete && <>No Results...</>}

                </div>
            </div>
        </>
    )
}